'use client';

import React, { useState } from 'react';
import { GlobalButton } from './GlobalButton';
import GlobalSidebar from './GlobalSidebar';
import { GlobalTextarea } from './GlobalTextarea';
import { useToast } from '../context/ToastProvider';
import { useTheme } from '../context/ThemeProvider';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useUser } from '../context/UserContext';
import { useUserProfile } from '../hooks/useUserProfile';
import LocalLLMTrust from './LocalLLMTrust';
import styles from './EntityGenerationSidebar.module.css';
import type { RecordType } from '../types';

interface GenerateEntitySidebarProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  entityType: RecordType;
  onSuccess?: () => void;
}

export default function GenerateEntitySidebar({
  isOpen,
  onClose,
  projectId,
  entityType,
  onSuccess
}: GenerateEntitySidebarProps) {
  const { t } = useLocale();
  const showToast = useToast();
  const { theme } = useTheme();
  const { user } = useUser();
  const { language } = useUserProfile();
  const isDarkMode = theme === 'dark';

  const [userPrompt, setUserPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Get localized entity type name
  const getEntityTypeName = (type: RecordType): string => {
    const nameMap: Record<RecordType, string> = {
      'URS': t('project_page_tab_urs') || 'Requirements',
      'FS': t('project_page_tab_fs') || 'Specifications',
      'RISK': t('project_page_tab_ra') || 'Risks',
      'TEST': t('project_page_tab_tests') || 'Tests',
      'SOP': t('project_page_tab_sops') || 'SOPs',
    };
    return nameMap[type] || type;
  };

  const handleGenerate = async () => {
    if (!userPrompt.trim()) {
      showToast(
        t('generate_entity_prompt_required') || 'Please enter a prompt to generate the entity',
        'error'
      );
      return;
    }

    if (!user?.uid) {
      showToast(
        t('generate_entity_user_not_authenticated') || 'User not authenticated',
        'error'
      );
      return;
    }

    setIsLoading(true);
    showToast(
      t('generate_entity_starting') || `Generating ${getEntityTypeName(entityType)}...`,
      'info'
    );

    try {
      // Use the deployed function URL
      const functionUrl = process.env.NEXT_PUBLIC_GENERATE_ENTITY_URL || 
        'https://europe-west3-thescon-app.cloudfunctions.net/generateEntity';

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectId,
          userPrompt: userPrompt.trim(),
          entityType,
          language: language || 'en',
        }),
      });

      if (!response.ok) {
        let errorData: any = {};
        try {
          errorData = await response.json();
        } catch (e) {
          const text = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${text}`);
        }
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        showToast(
          t('generate_entity_success') || `Successfully generated ${getEntityTypeName(entityType)}`,
          'success'
        );

        // Reset form
        setUserPrompt('');

        // Call success callback
        if (onSuccess) {
          onSuccess();
        }

        // Close sidebar after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('Entity generation error:', error);
      showToast(
        t('generate_entity_error') || `Failed to generate entity: ${errorMessage}`,
        'error'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <GlobalSidebar isOpen={isOpen} onClose={onClose}>
      <div className={styles.content}>
        {/* Header */}
        <div className={styles.header}>
          <img 
            src={isDarkMode ? "/assets/identity/logo-dark.svg" : "/assets/identity/logo.svg"} 
            alt="Thescon" 
            width={16} 
            height={16} 
          />
          <span>|</span>
          <h2>AI</h2>
        </div>

        {/* Prompt Section */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {t('generate_entity_prompt_label') || `Generate ${getEntityTypeName(entityType)}`}
          </h3>
          <p className={styles.sectionDescription}>
            {t('generate_entity_prompt_description') || 
              `Describe what ${getEntityTypeName(entityType)} you want to generate. Be specific about the requirements, functionality, test scenario, risk, or procedure you need.`}
          </p>
          <GlobalTextarea
            label={t('generate_entity_prompt_label') || 'Generation Prompt'}
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            placeholder={t('generate_entity_prompt_placeholder') || 
              `Describe the ${getEntityTypeName(entityType)} you want to generate...`}
            disabled={isLoading}
            rows={8}
          />
        </div>

        {/* Fire Button */}
        <div className={styles.actionSection}>
          <GlobalButton
            onClick={handleGenerate}
            disabled={isLoading || !userPrompt.trim()}
            isLoading={isLoading}
          >
            {isLoading 
              ? (t('generate_entity_processing') || 'Generating...')
              : (t('generate_entity_generate') || `Generate ${getEntityTypeName(entityType)}`)}
          </GlobalButton>
        </div>

        <LocalLLMTrust />
      </div>
    </GlobalSidebar>
  );
}
